import { useUser } from "@clerk/nextjs";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

/**
 * Cached query data belongs to the user it was fetched for, and cached rows now outlive a modal
 * close. Signing out through the account menu clears the cache itself, but Clerk can also swap the
 * signed-in user inside the same document (its own sign-out control, a session switch), which
 * leaves that data in place for whoever signs in next. Drop it whenever the identity changes.
 */
export const useIdentityScopedQueryCache = () => {
  const { isLoaded, user } = useUser();
  const queryClient = useQueryClient();
  const lastUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }
    const userId = user?.id ?? null;
    const previousUserId = lastUserIdRef.current;
    lastUserIdRef.current = userId;
    // First load has nothing cached under another identity, and a re-render with the same user must
    // not throw away the cache the app is reading.
    if (previousUserId === null || previousUserId === userId) {
      return;
    }
    void queryClient.cancelQueries();
    queryClient.removeQueries();
  }, [isLoaded, queryClient, user?.id]);
};
